import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MatchGroupNSwagMigrationService } from '../../api/migration/match-group-nswag-migration.service';
import { FacilitySearchResult } from '../../models/facility-search-result.model';
import { MatchGroupsResponse } from '../../models/match-group.model';
import {
    addMatchGroup,
    addMatchGroupFailure,
    addMatchGroupSuccess,
    deleteMatchGroup,
    deleteMatchGroupFailure,
    deleteMatchGroupItem,
    deleteMatchGroupItemFailure,
    deleteMatchGroupItemSuccess,
    deleteMatchGroupSuccess,
    getMatchGroupPermissions,
    getMatchGroupPermissionsFailure,
    getMatchGroupPermissionsSuccess,
    loadFacilitySearchResults,
    loadFacilitySearchResultsFailure,
    loadFacilitySearchResultsSuccess,
    loadMatchGroups,
    loadMatchGroupsFailure,
    loadMatchGroupsSuccess,
    updateMatchGroup,
    updateMatchGroupFailure,
    updateMatchGroupSuccess
} from '../actions/match-group.actions';

@Injectable()
export class MatchGroupEffects {
    constructor(private readonly _actions$: Actions, private readonly _matchGroupService: MatchGroupNSwagMigrationService) {}

    loadMatchGroups$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadMatchGroups),
            switchMap((action) => {
                return this._matchGroupService.loadMatchGroups().pipe(
                    map((response: MatchGroupsResponse) => loadMatchGroupsSuccess({ response: response })),
                    catchError((result) => of(loadMatchGroupsFailure({ error: result })))
                );
            })
        )
    );

    loadFacilitySearchResults$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadFacilitySearchResults),
            switchMap((action) => {
                return this._matchGroupService.loadFacilities(action.facilityOrSystem, action.name).pipe(
                    map((response: FacilitySearchResult) => loadFacilitySearchResultsSuccess({ response: response })),
                    catchError((result) => of(loadFacilitySearchResultsFailure({ error: result })))
                );
            })
        )
    );

    addMatchGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(addMatchGroup),
            switchMap((action) => {
                return this._matchGroupService.createGroup(action.name, action.facilityIds, action.systemIds).pipe(
                    map(() => addMatchGroupSuccess()),
                    catchError((result) => of(addMatchGroupFailure({ error: result })))
                );
            })
        )
    );

    reloadGroups$ = createEffect(() =>
        this._actions$.pipe(
            ofType(addMatchGroupSuccess, updateMatchGroupSuccess, deleteMatchGroupSuccess, deleteMatchGroupItemSuccess),
            map(() => loadMatchGroups())
        )
    );

    updateMatchGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateMatchGroup),
            switchMap((action) => {
                return this._matchGroupService
                    .updateGroup(action.id, action.name, action.facilityIds, action.systemIds, action.isStrike)
                    .pipe(
                        map(() => updateMatchGroupSuccess()),
                        catchError((result) => of(updateMatchGroupFailure({ error: result })))
                    );
            })
        )
    );

    deleteMatchGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteMatchGroup),
            switchMap((action) => {
                return this._matchGroupService.deleteGroup(action.id).pipe(
                    map(() => deleteMatchGroupSuccess()),
                    catchError((result) => of(deleteMatchGroupFailure({ error: result })))
                );
            })
        )
    );

    deleteMatchGroupItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteMatchGroupItem),
            switchMap((action) => {
                return this._matchGroupService.deleteGroupItem(action.facilityId, action.groupId, action.isStrike).pipe(
                    map(() => deleteMatchGroupItemSuccess()),
                    catchError((result) => of(deleteMatchGroupItemFailure({ error: result })))
                );
            })
        )
    );

    getMatchGroupPermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getMatchGroupPermissions),
            switchMap((action) => {
                return this._matchGroupService.getPermissions().pipe(
                    map((result) => getMatchGroupPermissionsSuccess({ permissions: result })),
                    catchError((result) => of(getMatchGroupPermissionsFailure({ error: result })))
                );
            })
        )
    );
}
